import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Form, FormControl, FormGroup} from "@angular/forms";
import ProjectEquityInstallmentFunding from "../../../interfaces/project-installment-funding";
import {EquityInstallmentService, UpdateInstallmentObservableMode} from "../../equity-installment.service";
import {ConfirmationModalComponent} from "../../../components/confirmation-modal/confirmation-modal.component";
import {MatDialog} from "@angular/material/dialog";
import ProjectInstallmentFundingConditions from "../../../interfaces/project-installment-funding-conditions";
import ProjectInstallmentFundingStatus from "../../../interfaces/project-installment-funding-status";
import {forkJoin, Observable, of, Subscription} from "rxjs";
import ProjectInstallmentFundingPackage from "../../../interfaces/project-installment-funding-package";
import ProjectInstallmentFundingPayment from "../../../interfaces/project-installment-funding-payment";
import {
  AddEquityInstallmentMode,
  AddInstallmentFundingModalComponent
} from "../add-installment-funding-modal/add-installment-funding-modal.component";
import {DatePipe} from "@angular/common";
import ProjectInstallmentFundingConditionStatus from "../../../interfaces/project-installment-funding-condition-status";
import Permission from "../../../interfaces/permission";
import {EmbeddedReportService} from "../../../components/embedded-report/embedded-report.service";
import EmbeddableReport from "../../../interfaces/embeddable-report";
import {
  EquityInstallmentSharepointService
} from "../equity-installment-documents-panel/equity-installment-sharepoint.service";
import ID from "../../../types/id";
import ProjectEquityInstallment from "../../../interfaces/project-equity-installment";
import {MatTooltip} from "@angular/material/tooltip";
import {DataFormatService} from "../../../services/data-format.service";
import ProjectInstallmentFundingConditionApproval
  from "../../../interfaces/project-installment-funding-condition-approval";

@Component({
    selector: 'equity-installment-funding',
    templateUrl: './equity-installment-funding.component.html',
    styleUrls: ['./equity-installment-funding.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EquityInstallmentFundingComponent implements OnInit, AfterViewInit{

  @Input() funding:ProjectEquityInstallmentFunding;
  @Input() parentInstallment: ProjectEquityInstallment
  @Output() updateInstallmentAmount: EventEmitter<any> = new EventEmitter<any>()
  @Output() removeFunding: EventEmitter<string> = new EventEmitter<string>()
  public get isExpanded(): boolean {
    return this.equityService.expandedFundings.has(this.funding.id.toString());
  }
  public allConditionsExpanded: boolean = false;
  public fundingTitle: string = ''
  public project:any;
  @Input() readOnly:boolean = false;
  @ViewChild('packageTooltip') packageTooltip: MatTooltip
  public fundingForm:FormGroup = new FormGroup({
    funding_number: new FormControl(),
    funding_amount: new FormControl(),
    total_adjusters: new FormControl(),
    status: new FormControl(),
    date: new FormControl()
  })

  public paymentForms:FormGroup[] = []
  public conditions:ProjectInstallmentFundingConditions[] = []
  public conditionForms: FormGroup[] = []
  public approvedConditions: number = 0;
  public packages: ProjectInstallmentFundingPackage[] = []
  public payments: ProjectInstallmentFundingPayment[] = []
  public fundingStatuses: ProjectInstallmentFundingStatus[] = []
  public conditionStatuses: ProjectInstallmentFundingConditionStatus[] = []
  public reports: EmbeddableReport[] = []
  private statusSub:Subscription
  private conditionsSub: Subscription;
  private conditionStatusSub:Subscription
  private paymentsSub: Subscription
  public userSub: Subscription
  private projectSub: Subscription
  private permissionsSub: Subscription
  private reportSub: Subscription
  private erroredFilesSub: Subscription
  private conditionApprovalsSub: Subscription
  public conditionApprovals: any = {}
  public permissions:any;
  public user: any;
  public invalidFiles: number = 0;
  public files:number = 0;
  public readOnlyStatus: string;

  constructor(public equityService: EquityInstallmentService, private dialog: MatDialog, private datePipe: DatePipe, private dataFormat: DataFormatService, public embeddableReportService: EmbeddedReportService, private sharepointService: EquityInstallmentSharepointService) {
  }

  ngOnInit() {
    this.fundingTitle = "Funding " + this.funding.funding_number
    this.packages.push(...this.funding.projectInstallmentFundingPackages)
    this.embeddableReportService.getReports()
    this.conditionsSub = this.equityService.projectFundingConditions$.subscribe((result) => {
      this.conditions = result[this.funding.id]
    })
    this.conditionApprovalsSub = this.equityService.projectConditionApprovals$.subscribe((result) => {
      this.conditionApprovals = result
    })
    this.statusSub = this.equityService.projectInstallmentFundingStatuses$.subscribe((result) => {
      this.fundingStatuses = result
    })
    this.conditionStatusSub = this.equityService.projectInstallmentConditionStatuses$.subscribe((result) => {
      this.conditionStatuses = result;
    })

    this.projectSub = this.equityService.project$.subscribe((result) => {
      this.project = result
    })

    this.userSub = this.equityService.user$.subscribe((result) => this.user = result)

    this.permissionsSub = this.equityService.permissions$.subscribe((result) => this.permissions = result)

    this.paymentsSub = this.equityService.projectFundingPayments$.subscribe((result) => {
      this.payments = result[this.funding.id]
      this.paymentForms = this._setPaymentForms(result[this.funding.id])
    })
    this.reportSub = this.embeddableReportService.reports$.subscribe((results) => {
      this.reports = results
    })
    this.conditions?.forEach((condition:ProjectInstallmentFundingConditions) => {
      let form = new FormGroup({
          id: new FormControl(condition.id),
          number: new FormControl(condition.condition_number),
          name: new FormControl(condition.name),
          include_in_package: new FormControl(condition.include_in_package),
          additional_notes: new FormControl(condition.additional_notes),
          nickname: new FormControl(condition.nickname),
          status: new FormControl(condition.projectInstallmentFundingConditionStatus !== null ? condition.projectInstallmentFundingConditionStatus.id : this.conditionStatuses.find((status: ProjectInstallmentFundingConditionStatus) => status.name == 'Due')?.id),
          date: new FormControl(condition.condition_status_date),
          notes: new FormControl(condition.notes)
      })
      this.conditionForms.push(form)
      if(condition.projectInstallmentFundingConditionStatus.name.toLowerCase() == 'approved'){
        this.approvedConditions += 1;
      }
    })
    this.conditions = [...this.conditions].sort((a:ProjectInstallmentFundingConditions, b: ProjectInstallmentFundingConditions) => {
      // const normalize = (val?: string) => (val ?? "").toLowerCase()
      //
      // const aClosed = normalize(a.projectInstallmentFundingConditionStatus?.name)==='received'
      // const bClosed = normalize(b.projectInstallmentFundingConditionStatus?.name)==='received'
      // if(aClosed !== bClosed){
      //   return aClosed ? 1 : -1
      // }
      return a.condition_number - b.condition_number
    })
    this.conditions.forEach((condition: ProjectInstallmentFundingConditions) => this.files += condition.projectInstallmentFundingConditionFiles.length)
    this.erroredFilesSub = this.equityService.erroredFiles$.subscribe((result) => {
      this.invalidFiles = 0;
      for(let key of Object.keys(result)){
        if(key===this.funding.id){
          for(let conKey of Object.keys(result[key])){
            this.invalidFiles += result[key][conKey].length
          }
        }
      }
    })
    if(!this.checkPermissions(this.permissions?.fundingStatusPermissions)){
      const selectedId = this.funding.projectInstallmentFundingStatus?.id
      this.fundingStatuses = this.fundingStatuses.filter((status: ProjectInstallmentFundingStatus) => status.name.toLowerCase() !== 'fully approved' || status.id === selectedId)
    }
    this._setFundingForm()
  }

  ngAfterViewInit() {
    if(this.funding.projectInstallmentFundingPayments.length==0){
      this.packageTooltip.show()
    }
  }


  public expandPanel() {
    const id = this.funding.id.toString();
    if (this.equityService.expandedFundings.has(id)) {
      this.equityService.expandedFundings.delete(id);
    } else {
      this.equityService.expandedFundings.add(id);
      this.sharepointService.openFolderFromFunding(
        this.parentInstallment.installment_number,
        this.funding.funding_number,
      );
    }
  }

  public _setFundingForm(){
    this.fundingForm.controls['funding_amount'].setValue(this.funding.funding_amount)
    this.fundingForm.controls['status'].setValue(this.funding.projectInstallmentFundingStatus !== null ? this.funding.projectInstallmentFundingStatus.id : null)
    this.fundingForm.controls['date'].setValue(this.funding.funding_status_date)
    this.fundingForm.controls['total_adjusters'].setValue(this.funding.total_adjuster_amount)
    this.readOnlyStatus = this.funding.projectInstallmentFundingStatus !== null ? this.funding.projectInstallmentFundingStatus.name : '--'
  }

  public _setPaymentForms(payments : ProjectInstallmentFundingPayment[]){
    let forms:FormGroup[] = []
    for(let p = 0; p < payments.length; p ++){
      let groupForm = new FormGroup({
        payment_id: new FormControl(payments[p].id),
        fund: new FormControl(payments[p].fund !== null ? payments[p].fund.name : null),
        certificatedOutlet: new FormControl(payments[p].certificatedOutlet !== null ? payments[p].certificatedOutlet.organization.name : null),
        warehouse: new FormControl(payments[p].warehouse),
        amount: new FormControl(payments[p].amount),
        adjuster: new FormControl(payments[p].adjuster)
      })

      forms.push(groupForm)
    }
    return forms;
  }

  public save(){
    const ops: Observable<any>[] =[]

    if(!this.readOnly && this.permissions?.fundingPermissions?._edit && this.fundingForm.dirty){
      ops.push(this.equityService.updateInstallmentFunding({
        id: this.funding.id,
        project_installment_funding_status_id: this.fundingForm.value.status,
        funding_amount: this.dataFormat.commaCurrencyToInt(this.fundingForm.value.funding_amount),
        funding_status_date: this.fundingForm.value.date,
        total_adjuster_amount: this.dataFormat.commaCurrencyToInt(this.fundingForm.value.total_adjusters),
      }))
    }

    if(!this.readOnly && this.permissions?.paymentPermissions?._edit){
      this.paymentForms.filter(pf => pf.dirty).forEach(pf => ops.push(this.equityService.updateFundingPayment({
        id: pf.value.payment_id,
        amount: this.dataFormat.commaCurrencyToInt(pf.value.amount),
        adjuster: this.dataFormat.commaCurrencyToInt(pf.value.adjuster),
      })));
    }
    if(!this.readOnly && this.permissions?.conditionPermissions?._edit){
      this.conditionForms.filter(cf => cf.dirty).forEach(cf => ops.push(this.equityService.updateFundingCondition({
        id: cf.value.id,
        condition_number: cf.value.number,
        name: cf.value.name,
        nickname: cf.value.nickname,
        include_in_package: cf.value.include_in_package,
        additional_notes: cf.value.additional_notes,
        notes: cf.value.notes,
        project_installment_funding_condition_status_id: cf.value.status,
        condition_status_date: cf.value.date,
      })));

    }


    return ops.length ? forkJoin(ops) : of(null);
  }

  public saveFunding(){
    let dataVar = {
      id: this.funding.id,
      project_installment_funding_status_id: this.fundingForm.value.status,
      funding_amount: this.dataFormat.commaCurrencyToInt(this.fundingForm.value.funding_amount),
      funding_status_date: this.fundingForm.value.date,
      total_adjuster_amount: this.dataFormat.commaCurrencyToInt(this.fundingForm.value.total_adjusters)
    }

    this.equityService.updateInstallmentFunding(dataVar).subscribe((response) => {
      this.equityService.openSnackBar('Funding updated', 'save-success')
      let rs = response as any;
      let resultData = rs.data.updateProjectInstallmentFunding
      this.equityService.updateInstallmentFundings(UpdateInstallmentObservableMode.UPDATE,resultData)
      this.updateInstallmentAmount.emit()
    })
  }

  public saveConditions(){
    let savedConditions = 0;
    this.conditionForms.forEach((form:FormGroup) => {
      let obj = {
        id: form.value.id,
        condition_number: form.value.number,
        name: form.value.name,
        nickname: form.value.nickname,
        include_in_package: form.value.include_in_package,
        additional_notes: form.value.additional_notes,
        notes: form.value.notes,
        project_installment_funding_condition_status_id: form.value.status,
        condition_status_date: form.value.date
      }
      this.equityService.updateFundingCondition(obj).subscribe((result) => {
        savedConditions += 1
        let rs = result as any;
        let resultData = rs.data.updateProjectInstallmentCondition
        this.equityService.updateFundingConditions(UpdateInstallmentObservableMode.UPDATE, resultData)
        if(savedConditions===this.conditionForms.length){
          this.equityService.openSnackBar('Condition updated.', 'save-success')
        }
      })
    })
  }

  public removeConditionForm(condition_id: string){
    this.conditionForms = this.conditionForms.filter((form: FormGroup) => form.value.id===condition_id)
  }

  public openRemoveFundingModal(){
    let dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      height: '200px',
      data: {
        modalTitle: 'Delete Funding ' + this.funding.funding_number,
        actionMessage: 'Are you sure you want to delete this installment funding?',
      }
    })

    dialogRef.afterClosed().subscribe((data) => {
      if(data.clicked === 'Ok'){
        this.equityService.deleteInstallmentFunding(this.funding.id.toString()).subscribe((response) => {
          let rs = response as any;
          let resultData = rs.data.deleteProjectInstallmentFunding
          this.equityService.updateInstallmentFundings(UpdateInstallmentObservableMode.REMOVE, resultData)
        })
      }
    })
  }

  public openRemovePaymentModal(row:any) {
    let dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      height: '200px',
      data: {
        modalTitle: 'Delete payment?',
        actionMessage: 'Are you sure you want to delete this payment?'
      }
    })

    dialogRef.afterClosed().subscribe((data) => {
      if(data.clicked === 'Ok'){
        this.equityService.deleteFundingPayment(row.value.payment_id).subscribe((result) => {
          this.equityService.openSnackBar('Removed payment', 'save-success')
          let filteredPayment = this.paymentForms.find((payment: FormGroup) => payment.value.id == row.value.id);
          if(filteredPayment){
            this.equityService.updatePayments(UpdateInstallmentObservableMode.REMOVE, filteredPayment.getRawValue(), this.funding.id)
          }
          this.paymentForms = this.paymentForms.filter((payment:FormGroup) => payment.value.id !== row.value.id)
        })
      }
    })
  }

  public updatePayment(){
    let savedForms = 0;
    for(let p = 0; p < this.paymentForms.length; p++){
      let paymentDetails = {
        id: this.paymentForms[p].value.payment_id,
        amount: this.dataFormat.commaCurrencyToInt(this.paymentForms[p].value.amount),
        adjuster: this.dataFormat.commaCurrencyToInt(this.paymentForms[p].value.adjuster)
      }
      this.equityService.updateFundingPayment(paymentDetails).subscribe((response:any) => {
        savedForms += 1
        if(savedForms === this.paymentForms.length){
          this.equityService.openSnackBar('Payment updated.', 'save-success')
          this.equityService.fetchGeneratedPayments(this.funding.id)
          this.updateInstallmentAmount.emit()
          this.equityService.refetchFunding(this.funding.id).subscribe((response:any) => {
            this.funding = response.data.project_installment_funding
            this.equityService.updateInstallmentFundings(UpdateInstallmentObservableMode.UPDATE, this.funding)
          })
        }
      })
    }
  }

  public generateFundingPayments(){
    this.equityService.generatePayments(this.funding.id).subscribe((data:any) => {
      this.equityService.fetchGeneratedPayments(this.funding.id, ()=> {
        this.equityService.openSnackBar('No payment rows created', 'save-fail')
      })
    })
  }

  public fetchConditionForm(condition: ProjectInstallmentFundingConditions){
    let returnedForm = this.conditionForms.filter((form:FormGroup) => form.value.id == condition.id);
    return returnedForm[0]
  }

  public openAddConditionModal(){
    let dialogRef = this.dialog.open(AddInstallmentFundingModalComponent, {
      width: '800px',
      data: {
        modalType: AddEquityInstallmentMode.CONDITION
      }
    })

    let saveSub = dialogRef.componentInstance.save.subscribe((result) => {
      let defaultStatus = this.conditionStatuses.find((status:ProjectInstallmentFundingConditionStatus) => status.name.toLowerCase() == 'due')
      let conditionNumber = this.conditions.length > 0 ? this.conditions[this.conditions.length - 1].condition_number + 1 : 1
      let conditionObj = {
        project_installment_funding_id: this.funding.id,
        name: result.name,
        nickname: result.nickname,
        condition_number: conditionNumber,
        notes: result.notes,
        project_installment_funding_condition_status_id: defaultStatus ? defaultStatus.id : null,
        condition_status_date: this.datePipe.transform(new Date(), 'yyyy-MM-dd')
      }

      this.equityService.createFundingCondition(conditionObj).subscribe((result) => {
        let rs = result as any;
        let resultData = rs.data.createProjectInstallmentCondition
        let obj = new FormGroup({
          id: new FormControl(resultData.id),
          number: new FormControl(resultData.condition_number),
          name: new FormControl(resultData.name),
          include_in_package: new FormControl(resultData.include_in_package),
          additional_notes: new FormControl(resultData.additional_notes),
          nickname: new FormControl(resultData.nickname),
          status: new FormControl(resultData.projectInstallmentFundingConditionStatus.id),
          date: new FormControl(resultData.condition_status_date),
          notes: new FormControl(resultData.notes)
        })
        this.conditionForms.push(obj)
        this.equityService.openSnackBar('Condition created.', 'save-success')
        this.equityService.updateFundingConditions(UpdateInstallmentObservableMode.ADD, resultData)
        dialogRef.close()
      })
      saveSub.unsubscribe();
    })
  }

  public toggleExpandAll(){
    this.allConditionsExpanded = !this.allConditionsExpanded;
  }

  public checkPermissions(permission: Permission, key?: string){
    return this.equityService.checkPermissions(permission, '_edit', key)
  }

  public generatePackage(funding_id:ID)
  {
    if(this.funding.projectInstallmentFundingPayments.length==0) return;
    this.equityService.generatePackage(funding_id);
  }

  public refreshPackageList(){
    this.packages = []
    this.equityService.refreshPackageList(this.funding.id).subscribe((result:any) => {
      let packages = result.data.getFundingPackages
      this.packages.push(...packages)
    })
  }

  private _liveApprovals(condition: any): ProjectInstallmentFundingConditionApproval[] {
    return this.conditionApprovals[condition?.id] ?? condition?.projectInstallmentFundingConditionApprovals ?? [];
  }

  public allApproved(condition: any): boolean {
    const approvals = this._liveApprovals(condition);
    return approvals.length > 0 &&
      approvals.some((a:ProjectInstallmentFundingConditionApproval) => a.projectInstallmentFundingConditionApprovalStatus?.name?.toLowerCase() === 'approved' && a.projectInstallmentFundingConditionApprovalType?.name?.toLowerCase() == 'senior asset manager');
  }

  public receivedAndAMApproved(condition:any):boolean {
    const status = condition?.projectInstallmentFundingConditionStatus?.name
    const approvals = this._liveApprovals(condition)
    let hasAMApproval = approvals.some((appr:ProjectInstallmentFundingConditionApproval) => appr.projectInstallmentFundingConditionApprovalStatus?.name.toLowerCase()==='approved' && appr.projectInstallmentFundingConditionApprovalType.name.toLowerCase()==='asset manager')
    return hasAMApproval && status.toLowerCase() === 'received'
  }

  public readyForApprovalSrAM(condition:any):boolean {
    const approvals = this._liveApprovals(condition)
    return approvals.some((appr:ProjectInstallmentFundingConditionApproval) => appr?.projectInstallmentFundingConditionApprovalType?.name?.toLowerCase()==='senior asset manager' && appr?.projectInstallmentFundingConditionApprovalStatus?.name?.toLowerCase()==='ready for approval')
  }


  public deletePackage(id: string){
    let packageSelect = this.packages.filter((pkg:ProjectInstallmentFundingPackage) => pkg.id == id)
    this.sharepointService.removeSiteFile(packageSelect[0])
    this.equityService.deletePackage(id).subscribe((result:any) => {
      this.packages = this.packages.filter((pkg:ProjectInstallmentFundingPackage) => pkg.id !== id)
      this.equityService.openSnackBar('Package removed', 'save-success')
    })
  }

  ngOnDestroy(){
    this.statusSub.unsubscribe()
    this.conditionStatusSub.unsubscribe()
    this.conditionsSub.unsubscribe()
    this.paymentsSub.unsubscribe()
    this.userSub.unsubscribe()
    this.permissionsSub.unsubscribe()
    this.reportSub.unsubscribe()
    this.projectSub.unsubscribe()
    this.conditionApprovalsSub.unsubscribe()
  }

}
